import { supabase } from '@/integrations/supabase/client';

export interface SavedMessage {
  id: string;
  user_id: string;
  message_id: string;
  chat_id: string;
  note: string | null;
  tags: string[];
  created_at: string;
  // Joined data
  message_content?: string;
  chat_title?: string;
}

export async function saveMessage(
  userId: string,
  messageId: string,
  chatId: string,
  data?: { note?: string; tags?: string[] }
): Promise<SavedMessage> {
  const { data: saved, error } = await supabase
    .from('saved_messages')
    .insert({
      user_id: userId,
      message_id: messageId,
      chat_id: chatId,
      note: data?.note || null,
      tags: data?.tags || [],
    })
    .select()
    .single();

  if (error) throw error;
  return saved as SavedMessage;
}

export async function listSavedMessages(
  userId: string,
  filters?: {
    tag?: string;
    search?: string;
  }
): Promise<SavedMessage[]> {
  let query = supabase
    .from('saved_messages')
    .select(`
      *,
      messages!inner (content),
      chats!inner (title)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (filters?.tag) {
    query = query.contains('tags', [filters.tag]);
  }

  const { data, error } = await query;

  if (error) throw error;
  
  // Transform the data to flatten the joined fields
  return (data || []).map((item: any) => ({
    ...item,
    message_content: item.messages?.content,
    chat_title: item.chats?.title,
    messages: undefined,
    chats: undefined,
  })) as SavedMessage[];
}

export async function updateSavedMessage(
  savedId: string,
  data: { note?: string; tags?: string[] }
): Promise<void> {
  const updateData: Record<string, any> = {};
  if (data.note !== undefined) updateData.note = data.note;
  if (data.tags !== undefined) updateData.tags = data.tags;

  const { error } = await supabase
    .from('saved_messages')
    .update(updateData)
    .eq('id', savedId);

  if (error) throw error;
}

export async function deleteSavedMessage(savedId: string): Promise<void> {
  const { error } = await supabase
    .from('saved_messages')
    .delete()
    .eq('id', savedId);

  if (error) throw error;
}

export async function isMessageSaved(
  userId: string,
  messageId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('saved_messages')
    .select('id')
    .eq('user_id', userId)
    .eq('message_id', messageId)
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

export async function getSavedMessageByMessageId(
  userId: string,
  messageId: string
): Promise<SavedMessage | null> {
  const { data, error } = await supabase
    .from('saved_messages')
    .select('*')
    .eq('user_id', userId)
    .eq('message_id', messageId)
    .maybeSingle();

  if (error) throw error;
  return data as SavedMessage | null;
}

export async function getAllTags(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('saved_messages')
    .select('tags')
    .eq('user_id', userId);

  if (error) throw error;
  
  const allTags = new Set<string>();
  (data || []).forEach((item: { tags: string[] }) => {
    (item.tags || []).forEach(tag => allTags.add(tag));
  });
  
  return Array.from(allTags).sort();
}
